import {
  AssignmentException,
  BrowseAndGoState,
  CardType,
  ClientPro,
  ExceptionAction,
  MultiChoiceCardModel,
  OperationModel
} from './types'
import { CARD_TYPE_LABEL } from './labels'

export function operationAssignedByDefault(operation: OperationModel, client: ClientPro): boolean {
  switch (operation.defaultAudience) {
    case 'ALL':
      return true
    case 'NONE':
      return false
    case 'CSE':
      return client.cseOrRelated
    case 'NON_CSE':
      return !client.cseOrRelated
    default:
      return false
  }
}

export function hasException(
  exceptions: AssignmentException[],
  clientId: string,
  objectType: AssignmentException['objectType'],
  objectId: string,
  action: ExceptionAction
): boolean {
  return exceptions.some(item =>
    item.clientId === clientId &&
    item.objectType === objectType &&
    item.objectId === objectId &&
    item.action === action
  )
}

export function operationAssigned(
  operation: OperationModel,
  client: ClientPro,
  exceptions: AssignmentException[]
): boolean {
  if (hasException(exceptions, client.id, 'OPERATION_MODEL', operation.id, 'REMOVE')) return false
  if (hasException(exceptions, client.id, 'OPERATION_MODEL', operation.id, 'ADD')) return true
  return operationAssignedByDefault(operation, client)
}

export function cardAssigned(
  card: MultiChoiceCardModel,
  operation: OperationModel | undefined,
  client: ClientPro,
  exceptions: AssignmentException[]
): boolean {
  if (!operation || !operationAssigned(operation, client, exceptions)) return false
  if (hasException(exceptions, client.id, 'MULTICHOICE_CARD_MODEL', card.id, 'REMOVE')) return false
  if (hasException(exceptions, client.id, 'MULTICHOICE_CARD_MODEL', card.id, 'ADD')) return true
  return card.defaultActive
}

export function effectiveProgramId(state: BrowseAndGoState, client: ClientPro): string | null {
  const override = state.exceptions.find(item =>
    item.clientId === client.id &&
    item.objectType === 'PROGRAM_BENEFITS_MODEL' &&
    item.action === 'ADD' &&
    state.programs.some(program => program.id === item.objectId)
  )
  if (override) return override.objectId
  const fallback = state.programs.find(program => program.isDefault)
  return fallback ? fallback.id : null
}

export interface ClientAssignmentSummary {
  client: ClientPro
  removedCards: string[]
  addedCards: string[]
  removedOperations: string[]
  addedOperations: string[]
  programLabel: string | null
  expertMode: boolean
  visible: boolean
}

function collapseCards(cards: MultiChoiceCardModel[]): string[] {
  const labels: string[] = []
  const types: CardType[] = ['MIXED', 'CB_ONLY', 'NO_CB']
  types.forEach(type => {
    const group = cards.filter(card => card.type === type)
    if (!group.length) return
    if (group.length > 2) {
      labels.push(`${CARD_TYPE_LABEL[type]} · ${group.length} modèles`)
    } else {
      group.forEach(card => labels.push(card.name))
    }
  })
  return labels
}

export function summarizeClient(state: BrowseAndGoState, client: ClientPro): ClientAssignmentSummary {
  const removedOperationModels: OperationModel[] = []
  const addedOperationModels: OperationModel[] = []
  const removedCardModels: MultiChoiceCardModel[] = []
  const addedCardModels: MultiChoiceCardModel[] = []

  state.operations.forEach(operation => {
    if (operation.family === 'URSSAF' && !client.cseOrRelated) return
    const assignedByDefault = operationAssignedByDefault(operation, client)
    const assigned = operationAssigned(operation, client, state.exceptions)
    if (assignedByDefault && !assigned) removedOperationModels.push(operation)
    if (!assignedByDefault && assigned) addedOperationModels.push(operation)
    if (!assigned) return

    state.cards
      .filter(card => card.operationModelId === operation.id)
      .forEach(card => {
        if (card.defaultActive && hasException(state.exceptions, client.id, 'MULTICHOICE_CARD_MODEL', card.id, 'REMOVE')) {
          removedCardModels.push(card)
        }
        if (!card.defaultActive && hasException(state.exceptions, client.id, 'MULTICHOICE_CARD_MODEL', card.id, 'ADD')) {
          addedCardModels.push(card)
        }
      })
  })

  const programId = effectiveProgramId(state, client)
  const program = state.programs.find(item => item.id === programId)
  const defaultProgram = state.programs.find(item => item.isDefault)
  const programLabel = program && defaultProgram && program.id !== defaultProgram.id ? program.name : null

  const visible = Boolean(
    removedOperationModels.length ||
    addedOperationModels.length ||
    removedCardModels.length ||
    addedCardModels.length ||
    programLabel ||
    client.expertMode
  )

  return {
    client,
    removedCards: collapseCards(removedCardModels),
    addedCards: collapseCards(addedCardModels),
    removedOperations: removedOperationModels.map(operation => operation.name),
    addedOperations: addedOperationModels.map(operation => operation.name),
    programLabel,
    expertMode: client.expertMode,
    visible
  }
}
